import React from 'react';
import PropTypes from 'prop-types';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

/**
 * NavbarTop
 * ----------
 * A simplified top navigation bar that displays only brand elements (logo + site name).
 *
 * Mobile behavior:
 * - Hidden on small screens (xs/sm/md). A compact mobile header is rendered by NavbarLeft to
 *   show the hamburger, logo, and site name on those breakpoints.
 *
 * Desktop behavior:
 * - Visible on large screens and above (lg+), showing the brand while the NavbarLeft
 *   provides the sidebar navigation to the left.
 *
 * Update:
 * - Reduced the fixed height from 100px to a more compact 64px to improve vertical space usage.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.redirectRoute='/'] - Kept for backward compatibility; no longer used here.
 * @param {string} [props.logo] - The brand logo source (URL or data URI).
 * @param {string} [props.siteName] - The site/application name to display next to the logo.
 * @returns {JSX.Element} The rendered NavbarTop component.
 */
export const NavbarTop = ({
	redirectRoute = '/',
	logo,
	siteName
}) => {
	const logoSrc = logo;
	const toolbarJustify = logoSrc ? 'space-between' : 'flex-start';

	return (
		<AppBar
			elevation={4}
			sx={{
				position: 'unset',
				top: 0,
				height: '64px',
				justifyContent: 'center',
				// Hide on mobile/tablet; show on large screens and up
				display: { xs: 'none', lg: 'flex' }
			}}
		>
			<Toolbar
				sx={{
					px: '20px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: toolbarJustify,
					minHeight: '64px !important',
					height: '64px'
				}}
			>
				{/* Brand: Logo + Site Name */}
				{logoSrc && (
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<img
							src={logoSrc}
							alt={siteName ? `${siteName} logo` : 'Site logo'}
							style={{ height: 40, width: 'auto', marginRight: 12 }}
						/>
						{siteName && (
							<Typography variant="h6" sx={{ color: '#296970', fontWeight: 600 }}>
								{siteName}
							</Typography>
						)}
					</Box>
				)}
				{/* Right side intentionally empty; user controls moved to NavbarLeft bottom */}
				<Box />
			</Toolbar>
		</AppBar>
	);
};

NavbarTop.propTypes = {
	redirectRoute: PropTypes.string,
	logo: PropTypes.string,
	siteName: PropTypes.string
};